import React, { FC, useMemo } from 'react'
import { Typography } from 'antd'
import { MapContainer, TileLayer, Marker, Popup, Tooltip } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import ListingPopup from '../VerticalPage/str/Listings/ListingPopup'
import type { ListingMarker } from '../VerticalPage/str/Listings/Listings'
import { formatAbbreviatedCurrency } from '../VerticalPage/str/utils'

const { Link } = Typography

interface STRListingMapMarker extends ListingMarker {
  hostName?: string
  entityId?: string
}

interface STRListingMapProps {
  entities: Record<string, unknown>[]
}

const dotIcon = new L.DivIcon({
  className: '',
  html: `<div style="
    width: 10px;
    height: 10px;
    background: #1E90FF;
    border: 3px solid #1E90FF78;
    border-radius: 50%;
  "></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
})

const DEFAULT_CENTER: L.LatLngExpression = [38.9096, -77.0434]

const buildMarkersFromEntities = (entities: Record<string, unknown>[]): STRListingMapMarker[] => {
  const markers: STRListingMapMarker[] = []

  entities.forEach((entity: Record<string, unknown>) => {
    const revenueSources = entity.revenueSources as Array<Record<string, unknown>> | undefined
    const totals = entity.totals as Record<string, unknown> | undefined
    const hostName = (totals?.hostName as string) || ''
    const entityId = entity.id as string

    revenueSources?.forEach(source => {
      const overview = source.overview as Record<string, unknown> | undefined
      const coords = overview?.coordinates as { lat: number; lng: number } | undefined
      if (!coords?.lat || !coords?.lng) return

      markers.push({
        id: (source.profileUrl as string) || `${entityId}-${Math.random()}`,
        platform: (source.platform as string) || '',
        lat: coords.lat,
        lng: coords.lng,
        title: (source.profileName as string) || '',
        estimatedRevenue: (overview?.totalRevenue as number) || 0,
        numberOfReviews: totals?.totalReviews as number | undefined,
        images: source.profileImageUrl ? [source.profileImageUrl as string] : [],
        hostName,
        entityId,
      })
    })
  })

  return markers
}

const STRListingMap: FC<STRListingMapProps> = ({ entities }) => {
  const markers = useMemo(() => buildMarkersFromEntities(entities), [entities])

  const mapCenter: L.LatLngExpression = useMemo(() => {
    if (markers.length === 0) return DEFAULT_CENTER
    const avgLat = markers.reduce((sum, m) => sum + m.lat, 0) / markers.length
    const avgLng = markers.reduce((sum, m) => sum + m.lng, 0) / markers.length
    return [avgLat, avgLng]
  }, [markers])

  if (markers.length === 0) return null

  return (
    <div style={{ marginBottom: 16 }}>
      <Typography.Text strong style={{ display: 'block', marginBottom: 8, fontSize: 14, color: '#666' }}>
        Listing Map
      </Typography.Text>
      <div style={{ height: 350, borderRadius: 8, overflow: 'hidden', border: '1px solid #f0f0f0' }}>
        <MapContainer
          center={mapCenter}
          zoom={10}
          style={{ width: '100%', height: '100%' }}
          zoomControl
          scrollWheelZoom={false}
          attributionControl={false}
        >
          <TileLayer
            url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          />
          {markers.map(marker => (
            <Marker key={marker.id} position={[marker.lat, marker.lng]} icon={dotIcon}>
              <Tooltip
                permanent
                interactive
                direction='top'
                offset={[0, -10]}
                className='str-revenue-tooltip'
                eventHandlers={{
                  click: e => {
                    const leafletMarker = e.target._source
                    if (leafletMarker) leafletMarker.openPopup()
                  },
                }}
              >
                {formatAbbreviatedCurrency(marker.estimatedRevenue)}
              </Tooltip>
              <Popup maxWidth={320} closeButton={false}>
                <div>
                  <ListingPopup
                    listing={marker}
                    hostSlot={
                      marker.hostName ? (
                        <Typography.Text type='secondary' style={{ fontSize: 14, display: 'block', marginBottom: 4 }}>
                          Host :{' '}
                          <Link
                            href={`/vertical/short-term-rentals/${marker.entityId}`}
                            target='_blank'
                            style={{ fontSize: 14 }}
                          >
                            {marker.hostName}
                          </Link>
                        </Typography.Text>
                      ) : undefined
                    }
                  />
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </div>
  )
}

export default STRListingMap
